import { useQuery } from "@tanstack/react-query"
import { formatDate, isValid, parseISO } from "date-fns"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Badge } from "design-system/atoms/Badge"
import { Separator } from "design-system/atoms/Separator"
import { Skeleton } from "design-system/atoms/Skeleton"
import { RefreshCw } from "lucide-react"
import { amphoraTicketQuery } from "modules/support/lib/amphoraTicketQuery"
import { useTranslation } from "react-i18next"
import {
  AmphoraApiTicketMessageResponseDataSchema,
  AmphoraApiTicketResponseDataSchema,
  AttachmentSchema,
} from "schema"
import { z } from "zod"

type Props = {
  enabled: boolean
} & ({ ticketId: string } | { issueId: string })

type Ticket = z.infer<typeof AmphoraApiTicketResponseDataSchema>
type Message = z.infer<typeof AmphoraApiTicketMessageResponseDataSchema>
type Attachment = NonNullable<Message["attached_files"]>[number]

const MESSAGE_FIELDS = ["side", "type", "source", "posted_via"] as const

const TICKET_FIELD_GROUPS = [
  {
    key: "general",
    fields: [
      "company_id",
      "order_name",
      "subcategory",
      "source",
      "priority",
      "gap",
      "last_message_date",
      "update_time",
      "folder_name",
      "folder_date",
    ],
  },
  {
    key: "assignment",
    fields: [
      "client_assigned",
      "amphora_assigned",
      "started_by",
      "read_by",
      "escalated_at",
      "escalated_to",
      "escalated_by",
      "escalation_reason",
    ],
  },
  {
    key: "references",
    fields: [
      "external_id",
      "zendesk_id",
      "all_zendesk_id",
      "external_integration",
      "last_external_pointer",
    ],
  },
] as const

const DATE_FIELDS = new Set(["last_message_date", "update_time", "escalated_at", "folder_date"])

export function AmphoraTicketPanel(props: Props) {
  const { t } = useTranslation()
  const { data, dataUpdatedAt, isError, isFetching, isPending } = useQuery(
    amphoraTicketQuery(props),
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {isFetching && <RefreshCw className="size-3.5 animate-spin" />}
        {dataUpdatedAt > 0 && (
          <span>
            {t("support.timeline.amphoraActivity.api.fetchedAt", {
              time: formatDate(dataUpdatedAt, "p"),
            })}
          </span>
        )}
      </div>
      {isPending && isFetching && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )}
      {isError && <EmptySlate message={t("support.timeline.amphoraActivity.api.fetchError")} />}
      {data &&
        (data.tickets.length > 0 ? (
          <Accordion type="multiple" className="flex flex-col gap-3">
            {data.tickets.map((ticket, ticketIndex) => (
              <AccordionItem
                key={ticketIndex}
                value={`ticket-${ticketIndex}`}
                className="rounded-md border last:border-b"
              >
                <AccordionTrigger className="py-3">
                  <div className="flex w-full min-w-0 items-center justify-between gap-4">
                    <span>
                      {ticket.source || t("support.timeline.amphoraActivity.api.unknownSource")}
                    </span>
                    <div className="flex flex-wrap items-center justify-end gap-4">
                      <Badge variant="secondary">
                        {t("support.timeline.amphoraActivity.api.messageCount", {
                          count: ticket.messages.length,
                        })}
                      </Badge>
                      {ticket.messages[0]?.date && (
                        <span className="text-xs font-normal text-muted-foreground">
                          {formatApiDate(ticket.messages[0].date)}
                        </span>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 pt-3">
                  <div className="flex flex-col gap-2">
                    {ticket.messages.length === 0 ? (
                      <EmptySlate
                        message={t("support.timeline.amphoraActivity.api.emptyMessages")}
                      />
                    ) : (
                      ticket.messages.map((message, index) => (
                        <MessageRow key={`${message.date}-${index}`} message={message} />
                      ))
                    )}
                  </div>
                  <Separator />
                  <TicketDetails ticket={ticket} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <EmptySlate message={t("support.timeline.amphoraActivity.api.notFound")} />
        ))}
    </div>
  )
}

function MessageRow({ message }: { message: Message }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">
            {message.user || t("support.timeline.amphoraActivity.api.unknownUser")}
          </span>
          {message.hide && (
            <Badge variant="outline">{t("support.timeline.amphoraActivity.api.hidden")}</Badge>
          )}
          {message.is_file && (
            <Badge variant="secondary">
              {t("support.timeline.amphoraActivity.api.fileMessage")}
            </Badge>
          )}
        </div>
        {message.date && (
          <span className="text-xs text-muted-foreground">{formatApiDate(message.date)}</span>
        )}
      </div>
      <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
        {message.message || t("support.timeline.amphoraActivity.emptyMessage")}
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {MESSAGE_FIELDS.map(
          (field) =>
            message[field] && (
              <span key={field} className="break-words">
                {t(`support.timeline.amphoraActivity.api.messageFields.${field}`, {
                  value: message[field],
                })}
              </span>
            ),
        )}
      </div>
      {message.attached_files?.map(
        (attachment, index) =>
          attachment !== null && <MessageAttachment key={index} attachment={attachment} />,
      )}
    </div>
  )
}

function MessageAttachment({ attachment }: { attachment: Attachment }) {
  const { t } = useTranslation()
  const parsed = AttachmentSchema.safeParse(attachment)

  if (parsed.success && parsed.data.download_url) {
    const url = parsed.data.download_url
    const isHttpUrl = url.startsWith("https://") || url.startsWith("http://")

    if (isHttpUrl) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-fit break-all text-sm text-support underline"
        >
          {parsed.data.original_file_name ??
            parsed.data.file_name ??
            t("support.timeline.amphoraActivity.attachment")}
        </a>
      )
    }
  }

  return <ApiValue value={attachment} />
}

function TicketDetails({ ticket }: { ticket: Ticket }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4">
      {TICKET_FIELD_GROUPS.map((group) => (
        <div key={group.key} className="flex flex-col gap-2">
          <span className="text-sm font-medium">
            {t(`support.timeline.amphoraActivity.api.groups.${group.key}`)}
          </span>
          <dl className="flex flex-col divide-y">
            {group.fields.map((field) => (
              <div key={field} className="grid gap-2 py-2 sm:grid-cols-[160px_1fr]">
                <dt className="text-xs font-medium text-muted-foreground">
                  {t(`support.timeline.amphoraActivity.api.fields.${field}`)}
                </dt>
                <dd className="min-w-0">
                  <ApiValue value={ticket[field]} isDate={DATE_FIELDS.has(field)} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  )
}

function ApiValue({ value, isDate = false }: { value: unknown; isDate?: boolean }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-xs text-muted-foreground">{"-"}</span>
  }

  if (typeof value === "object") {
    return (
      <pre className="whitespace-pre-wrap break-all rounded-md bg-muted p-2 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    )
  }

  return (
    <span className="whitespace-pre-wrap break-words text-xs">
      {isDate && typeof value === "string" ? formatApiDate(value) : String(value)}
    </span>
  )
}

function EmptySlate({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">
      {message}
    </div>
  )
}

function formatApiDate(value: string) {
  const date = parseISO(value)

  return isValid(date) ? formatDate(date, "Pp") : value
}
